import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  BrainCircuit, 
  CloudCog, 
  ShieldCheck, 
  Fingerprint, 
  LineChart, 
  Layers3 
} from 'lucide-react';

const EcommerceTechnology = () => {
  return (
    <section className="py-16 md:py-28 bg-gradient-to-b from-slate-50 to-white">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Zukunftsweisende E-Commerce Technologien
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Wir setzen auf innovative Technologien und Ansätze, die deine E-Commerce-Plattform 
            zukunftssicher, leistungsstark und skalierbar machen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <Card className="bg-white border border-slate-100 hover:border-blue-200 transition-all duration-300">
            <CardContent className="p-6 flex flex-col items-start h-full">
              <div className="rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 p-3 mb-4">
                <BrainCircuit className="h-7 w-7 text-violet-700" />
              </div>
              <h3 className="text-xl font-bold mb-3">KI & Machine Learning</h3>
              <p className="text-gray-600 mb-3">
                Intelligente Systeme für personalisierte Produktempfehlungen, dynamische Preisgestaltung und 
                prädiktives Marketing basierend auf Nutzerverhalten und Markttrends.
              </p>
              <div className="mt-auto pt-4">
                <span className="text-xs font-medium py-1 px-3 rounded-full bg-violet-50 text-violet-700">
                  Erhöht Conversion um bis zu 35%
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-100 hover:border-blue-200 transition-all duration-300">
            <CardContent className="p-6 flex flex-col items-start h-full">
              <div className="rounded-full bg-gradient-to-br from-blue-100 to-cyan-100 p-3 mb-4">
                <CloudCog className="h-7 w-7 text-blue-700" />
              </div>
              <h3 className="text-xl font-bold mb-3">Microservices & API-first</h3>
              <p className="text-gray-600 mb-3">
                Modular aufgebaute Systeme mit entkoppelten Services für maximale Flexibilität, schnellere 
                Implementierung neuer Features und verbesserte Skalierbarkeit.
              </p>
              <div className="mt-auto pt-4">
                <span className="text-xs font-medium py-1 px-3 rounded-full bg-blue-50 text-blue-700">
                  90% schnellere Feature-Implementierung
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-100 hover:border-blue-200 transition-all duration-300">
            <CardContent className="p-6 flex flex-col items-start h-full">
              <div className="rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 p-3 mb-4">
                <LineChart className="h-7 w-7 text-emerald-700" />
              </div>
              <h3 className="text-xl font-bold mb-3">Echtzeit-Analytik</h3>
              <p className="text-gray-600 mb-3">
                Fortschrittliche Analyse-Tools liefern Echtzeitdaten zu Nutzerverhalten, Verkaufstrends und 
                Kampagnenperformance für datengetriebene Entscheidungen.
              </p>
              <div className="mt-auto pt-4">
                <span className="text-xs font-medium py-1 px-3 rounded-full bg-emerald-50 text-emerald-700">
                  Erhöht ROI um bis zu 28%
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-100 hover:border-blue-200 transition-all duration-300">
            <CardContent className="p-6 flex flex-col items-start h-full">
              <div className="rounded-full bg-gradient-to-br from-amber-100 to-yellow-100 p-3 mb-4">
                <Layers3 className="h-7 w-7 text-amber-700" />
              </div>
              <h3 className="text-xl font-bold mb-3">Headless Commerce</h3>
              <p className="text-gray-600 mb-3">
                Trennung von Frontend und Backend für maximale Designfreiheit, optimale Performance und 
                nahtlose Integration mit allen digitalen Touchpoints.
              </p>
              <div className="mt-auto pt-4">
                <span className="text-xs font-medium py-1 px-3 rounded-full bg-amber-50 text-amber-700">
                  65% bessere Performance-Werte
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-100 hover:border-blue-200 transition-all duration-300">
            <CardContent className="p-6 flex flex-col items-start h-full">
              <div className="rounded-full bg-gradient-to-br from-red-100 to-rose-100 p-3 mb-4">
                <ShieldCheck className="h-7 w-7 text-red-700" />
              </div>
              <h3 className="text-xl font-bold mb-3">Next-Gen Sicherheit</h3>
              <p className="text-gray-600 mb-3">
                Fortschrittliche Sicherheitsprotokolle, KI-basierte Betrugserkennung und End-to-End-Verschlüsselung 
                schützen sensible Kundendaten und Transaktionen.
              </p>
              <div className="mt-auto pt-4">
                <span className="text-xs font-medium py-1 px-3 rounded-full bg-red-50 text-red-700">
                  99.9% Schutz vor Cyberangriffen
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-100 hover:border-blue-200 transition-all duration-300">
            <CardContent className="p-6 flex flex-col items-start h-full">
              <div className="rounded-full bg-gradient-to-br from-slate-100 to-gray-100 p-3 mb-4">
                <Fingerprint className="h-7 w-7 text-slate-700" />
              </div>
              <h3 className="text-xl font-bold mb-3">Hyper-Personalisierung</h3>
              <p className="text-gray-600 mb-3">
                Individualisierung auf Basis von Echtzeit-Daten, Präferenzen und Verhalten für maßgeschneiderte 
                Kundenerlebnisse, die Conversion und Kundenbindung deutlich steigern.
              </p>
              <div className="mt-auto pt-4">
                <span className="text-xs font-medium py-1 px-3 rounded-full bg-slate-50 text-slate-700">
                  42% höhere Kundenbindungsrate
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default EcommerceTechnology; 